<script lang="ts" setup>
import {computed, onBeforeMount, ref} from "vue";
import {useCompanyCreateStore} from "@/stores/create.store.ts";
import {Source} from "@/domain/type/source.type.ts";
import {CreateStatus} from "@/domain/type/create.type.ts";
import Luhn from "luhn-js";

const props = defineProps<{
  target: Source,
  type: string
}>();

const siret = ref<string>('')
const companyName = ref<string>('');

const store = useCompanyCreateStore();
const showFullForm = ref<boolean>(false);

onBeforeMount(() => {
  store.mainTarget = props.target;
})

const error = computed(() => {
  if (![Source.EVA, Source.AMMON].includes(props.target)) {
    showFullForm.value = false;
    return 'Le type de source doit être EVA ou AMMON pour créer une société';
  }
  if (store.createStatus === CreateStatus.noDataFound) {
    return '';
  }
  showFullForm.value = false;

  return null;
})


function checkSiret(value: string): true | string {
  if (undefined === value || value.length === 0) {
    return 'Siret invalide';
  }
  if (14 === value.length && Luhn.isValid(value)) {
    return true
  }

  return 'Siret invalide'
}

const form = ref<HTMLFormElement>()

async function submit() {
  form.value?.validate().then((isValid: { valid: boolean, errors: { id: string | number; errorMessages: string[] }[] }) => {
    if (isValid.valid) {
      if (store.manualForm) {
        store.createManually(siret.value, companyName.value)
      } else {
        store.createFromSiren(siret.value)
      }
    }
  })
}

function checkCompany(value: string): true | string {
  if (value.length > 0) {
    return true;
  }

  return 'Nom de la société invalide';
}

function onSiretUpdate(value: string) {
  siret.value = value.replace(/\s/g, '')
  store.createStatus = CreateStatus.none
  store.manualForm = false
}

</script>

<template>
  <v-form ref="form" validate-on="submit" @submit.prevent="submit">
    <v-card class="createCard" min-width="500">
      <v-card-title>
        Créer une nouvelle société
      </v-card-title>
      <v-card-text class="pb-0">
        <v-text-field
          v-model="siret"
          :error="store.statusIsError"
          :error-messages="error"
          :hint="store.manualForm ? '' : store.createStatusText"
          :persistent-hint="true"
          :rules="[checkSiret]"
          class="siretInput"
          label="Saisissez le siret"
          variant="outlined"
          @update:modelValue="onSiretUpdate"
        />
        <v-expand-transition>
          <v-fade-transition>
            <div v-if="store.manualForm">
              <v-alert class="mb-6">Cette entreprise est anonymisée dans le répertoire sirène. <br/>Veuillez saisir son nom manuellement.</v-alert>
              <v-text-field
                v-model="companyName"
                :error-messages="error"
                :hint="store.createStatusText"
                :persistent-hint="true"
                :rules="[checkCompany]"
                class="companyNameInput"
                label="Saisissez le nom de la société"
                variant="outlined"
              />
            </div>
          </v-fade-transition>
        </v-expand-transition>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn :loading="store.loading" text="Créer" type="submit" variant="flat"/>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<style scoped>

</style>
